if (!customElements.get('dyefor-party-bundle')) {
  customElements.define('dyefor-party-bundle', class DyeforPartyBundle extends HTMLElement {
    constructor() {
      super();
      this.balloonProducts = new Map();
      this.balloonVariant = null;
      this.mainVariant = null;
    }

    connectedCallback() {
      this.sectionElement = this.closest('[data-section]');
      this.infoWrapper = this.closest('.product__info-wrapper');
      this.sectionElement?.classList.add('product-page--party-bundle-active');
      this.infoWrapper?.classList.add('dyefor-party-bundle-enabled');
      this.mainVariants = this.parseJson('[data-main-variants]') || [];
      this.bundleGalleryImages = this.parseJson('[data-bundle-gallery-images]') || {};
      this.companionProduct = this.parseJson('[data-companion-product]') || { variants: [] };
      this.balloonProduct = this.parseJson('[data-balloon-product]') || { variants: [] };
      this.form = this.sectionElement?.querySelector('form[data-type="add-to-cart-form"]');
      this.modeInputs = Array.from(this.querySelectorAll('[data-bundle-mode]'));
      this.sizeInputs = Array.from(this.querySelectorAll('[data-wrap-size-input]'));
      this.singleDescription = this.querySelector('[data-single-description]');
      this.sizeOptionIndex = Number(this.dataset.sizeOptionIndex || 0);
      this.variantPicker = this.infoWrapper?.querySelector('variant-selects, variant-radios');
      const nativeSizeControl = this.findNativeSizeControl();
      this.nativeSizeWrapper = nativeSizeControl.wrapper;
      this.nativeSizeSelect = nativeSizeControl.select;
      this.nativeSizeRadios = nativeSizeControl.radios;
      this.nativeSizeWrappers = Array.from(this.infoWrapper?.querySelectorAll(
        '.product-form__input[data-option_name="Paper Size"], .product-form__input[data-option_name="Size"]'
      ) || []);
      this.companionSelect = this.querySelector('[data-companion-variant]');
      this.companionOptions = this.querySelector('[data-companion-options]');
      this.balloonOptions = this.querySelector('[data-balloon-options]');
      this.balloonColour = this.querySelector('[data-balloon-colour]');
      this.balloonNumber = this.querySelector('[data-balloon-number]');
      this.note = this.querySelector('[data-bundle-note]');
      this.errorElement = this.querySelector('[data-bundle-error]');
      this.productForm = this.infoWrapper?.querySelector('product-form');
      this.dynamicCheckout = this.infoWrapper?.querySelector('.shopify-payment-button');
      this.freeShippingThreshold = Number(this.dataset.freeShippingThreshold || 3000);
      this.balloonPrice = Number(this.dataset.balloonDefaultPrice || 1499);
      this.gallery = this.sectionElement?.querySelector('media-gallery-colors');
      this.galleryPreviewId = `${this.dataset.sectionId || 'product'}-party-bundle-preview`;
      this.restoreMediaId = null;

      if (this.balloonProduct.handle) {
        this.balloonProducts.set(this.balloonProduct.handle, this.balloonProduct);
      }
      this.selectInitialBalloonVariant();

      this.modeInputs.forEach((input) => input.addEventListener('change', () => (
        this.onModeChange({ revealDetails: true })
      )));
      this.sizeInputs.forEach((input) => input.addEventListener('change', () => this.onSizeChange(input)));
      this.companionSelect?.addEventListener('change', () => this.render());
      this.balloonColour?.addEventListener('change', () => this.onBalloonChange(true));
      this.balloonNumber?.addEventListener('change', () => this.onBalloonChange(false));

      this.onVariantChanged = (event) => {
        const changedVariant = this.mainVariants.find((variant) => Number(variant.id) === Number(event.detail?.id));
        if (!changedVariant) return;
        this.mainVariant = changedVariant;
        this.captureGalleryRestoreMedia(true);
        this.syncSizeState();
        this.render();
      };
      document.addEventListener('variantchanged', this.onVariantChanged);

      this.onAIProductPreviewReady = (event) => {
        this.applyAIBundleSwatches(event.detail?.preview);
        if (this.isAIDesignProduct) this.syncGalleryMedia();
      };
      document.addEventListener('dyefor:ai-product-preview-ready', this.onAIProductPreviewReady);

      this.nativeSizeWrappers.forEach((wrapper) => {
        wrapper.classList.add('dyefor-party-bundle__native-size');
        wrapper.setAttribute('aria-hidden', 'true');
      });

      this.refreshMainVariant();
      this.syncSizeState();
      this.onModeChange();

      const existingAIPreview = this.getAIGalleryPreview();
      if (existingAIPreview) {
        this.applyAIBundleSwatches({
          url: existingAIPreview.currentSrc || existingAIPreview.src,
          label: existingAIPreview.alt
        });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('variantchanged', this.onVariantChanged);
      document.removeEventListener('dyefor:ai-product-preview-ready', this.onAIProductPreviewReady);
      this.nativeSizeWrappers?.forEach((wrapper) => {
        wrapper.classList.remove('dyefor-party-bundle__native-size');
        wrapper.removeAttribute('aria-hidden');
      });
      this.sectionElement?.classList.remove('product-page--party-bundle-active');
      this.infoWrapper?.classList.remove('dyefor-party-bundle-enabled');
      this.removeGalleryPreview();
    }

    get isAIDesignProduct() {
      const aiHandlesByRole = {
        wrap: 'custom-ai-wrapping-paper',
        banner: 'custom-ai-banner'
      };
      const role = this.dataset.role;
      return Boolean(aiHandlesByRole[role] && this.dataset.mainHandle === aiHandlesByRole[role]);
    }

    getAIGalleryPreview() {
      const viewer = this.gallery?.elements?.viewer || this.gallery;
      return viewer?.querySelector('[data-dyefor-ai-product-preview="1"]')
        || viewer?.querySelector('[data-dyefor-ai-product-preview]');
    }

    applyAIBundleSwatches(preview) {
      if (!this.isAIDesignProduct) return;

      let source;
      try {
        const candidate = new URL(String(preview?.url || ''), window.location.href);
        if (candidate.protocol !== 'https:') return;
        source = candidate.href;
      } catch (error) {
        return;
      }

      const fallbackLabel = this.dataset.role === 'banner' ? 'Your AI banner design' : 'Your AI wrapping paper design';
      const label = String(preview?.label || '').trim() || fallbackLabel;
      this.querySelectorAll('[data-dyefor-ai-bundle-swatch]').forEach((swatch) => {
        const visual = document.createElement('span');
        visual.className = 'dyefor-party-bundle__visual-item dyefor-party-bundle__visual-item--composite';

        const image = document.createElement('img');
        image.src = source;
        image.alt = label;
        image.loading = 'eager';
        image.decoding = 'async';
        image.setAttribute('data-dyefor-ai-bundle-image', '');

        visual.appendChild(image);
        swatch.replaceChildren(visual);
      });
    }

    parseJson(selector) {
      const element = this.querySelector(selector);
      if (!element) return null;

      try {
        return JSON.parse(element.textContent);
      } catch (error) {
        console.error('Dyefor party bundle data could not be read.', error);
        return null;
      }
    }

    get selectedMode() {
      return this.modeInputs.find((input) => input.checked)?.value || 'single';
    }

    refreshMainVariant() {
      const variantId = Number(this.form?.querySelector('[name="id"]')?.value);
      this.mainVariant = this.mainVariants.find((variant) => Number(variant.id) === variantId)
        || this.mainVariant
        || this.mainVariants[0];
    }

    findNativeSizeControl() {
      const emptyControl = { wrapper: null, select: null, radios: [] };
      if (!this.variantPicker || !this.sizeInputs.length) return emptyControl;

      if (this.variantPicker.matches('variant-selects')) {
        const optionKey = `option${this.sizeOptionIndex + 1}`;
        const select = this.variantPicker.querySelector(`select[data-option="${optionKey}"]`)
          || Array.from(this.variantPicker.querySelectorAll('select'))[this.sizeOptionIndex]
          || null;

        return {
          wrapper: select?.closest('.product-form__input') || null,
          select,
          radios: []
        };
      }

      const wrapper = Array.from(this.variantPicker.querySelectorAll('fieldset'))[this.sizeOptionIndex] || null;
      return {
        wrapper,
        select: null,
        radios: wrapper ? Array.from(wrapper.querySelectorAll('input[type="radio"]')) : []
      };
    }

    getNativeSizeValue() {
      if (this.nativeSizeSelect) return this.nativeSizeSelect.value;
      return this.nativeSizeRadios.find((radio) => radio.checked)?.value;
    }

    getCurrentNativeOptions() {
      if (this.variantPicker?.matches('variant-selects')) {
        return Array.from(this.variantPicker.querySelectorAll('select[name^="options["]'))
          .map((select) => select.value);
      }

      if (this.variantPicker?.matches('variant-radios')) {
        return Array.from(this.variantPicker.querySelectorAll('fieldset')).map((fieldset) => (
          fieldset.querySelector('input[type="radio"]:checked')?.value || ''
        ));
      }

      return Array.from(this.mainVariant?.options || []);
    }

    getVariantForSize(value) {
      const selectedOptions = this.getCurrentNativeOptions();
      selectedOptions[this.sizeOptionIndex] = value;

      return this.mainVariants.find((variant) => (
        variant.options?.every((option, index) => option === selectedOptions[index])
      )) || null;
    }

    updateSizeAvailability() {
      this.sizeInputs.forEach((input) => {
        const variant = this.getVariantForSize(input.value);
        const available = Boolean(variant && variant.available !== false);
        const choice = input.closest('.dyefor-party-bundle__size-choice');
        const price = choice?.querySelector('.dyefor-party-bundle__size-price');

        input.dataset.variantId = variant?.id || '';
        input.disabled = !available;
        choice?.classList.toggle('is-disabled', !available);
        if (available) {
          input.removeAttribute('aria-disabled');
          choice?.removeAttribute('aria-disabled');
        } else {
          input.setAttribute('aria-disabled', 'true');
          choice?.setAttribute('aria-disabled', 'true');
        }
        if (price) price.textContent = available ? this.formatMoney(Number(variant.price || 0)) : 'Sold out';
      });
    }

    updateNativeSize(value) {
      if (this.nativeSizeSelect) {
        const optionExists = Array.from(this.nativeSizeSelect.options).some((option) => option.value === value);
        if (!optionExists || this.nativeSizeSelect.value === value) return false;

        this.nativeSizeSelect.value = value;
        this.nativeSizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      const radio = this.nativeSizeRadios.find((input) => input.value === value);
      if (!radio || radio.checked) return false;

      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    onSizeChange(input) {
      if (!input || input.disabled) return;

      const selectedVariant = this.getVariantForSize(input.value);
      if (!selectedVariant || selectedVariant.available === false) return;

      this.mainVariant = selectedVariant;
      this.syncSizeState(input.value);

      if (this.updateNativeSize(input.value)) return;

      const variantInput = this.form?.querySelector('[name="id"]');
      if (variantInput) variantInput.value = selectedVariant.id;

      this.render();
    }

    syncSizeState(value) {
      if (!this.sizeInputs.length) return;

      this.updateSizeAvailability();

      const selectedValue = value
        || this.getNativeSizeValue()
        || this.mainVariant?.options?.[this.sizeOptionIndex]
        || this.mainVariant?.[`option${this.sizeOptionIndex + 1}`];

      this.sizeInputs.forEach((input) => {
        const selected = input.value === selectedValue;
        input.checked = selected;
        input.closest('.dyefor-party-bundle__size-choice')?.classList.toggle('is-selected', selected);
      });

      this.updateSingleDescription(selectedValue);
    }

    updateSingleDescription(value) {
      if (!this.singleDescription || !value) return;

      const size = String(value).toLowerCase();
      if (size.includes('3 sheet')) {
        this.singleDescription.textContent = 'Three sheets of personalised wrapping paper';
      } else if (size.includes('small roll')) {
        this.singleDescription.textContent = 'One small roll of personalised wrapping paper';
      } else if (size.includes('large roll')) {
        this.singleDescription.textContent = 'One large roll of personalised wrapping paper';
      } else {
        this.singleDescription.textContent = 'One sheet of personalised wrapping paper';
      }
    }

    getCompanionVariant() {
      const selectedId = Number(this.companionSelect?.value);
      return this.companionProduct.variants.find((variant) => Number(variant.id) === selectedId)
        || this.companionProduct.variants.find((variant) => variant.available)
        || this.companionProduct.variants[0];
    }

    selectInitialBalloonVariant() {
      const selectedId = Number(this.balloonProduct.selectedVariantId);
      this.balloonVariant = this.balloonProduct.variants.find((variant) => (
        Number(variant.id) === selectedId && variant.available !== false
      )) || this.balloonProduct.variants.find((variant) => variant.available !== false) || null;

      if (this.balloonVariant) {
        this.balloonPrice = Number(this.balloonVariant.price || this.balloonPrice);
        this.updateBalloonPreview(this.balloonVariant, this.balloonProduct);
      }
    }

    async onModeChange({ revealDetails = false } = {}) {
      this.clearError();
      const mode = this.selectedMode;
      this.captureGalleryRestoreMedia(true);

      this.querySelectorAll('.dyefor-party-bundle__choice').forEach((choice) => {
        choice.classList.toggle('is-selected', choice.querySelector('input')?.checked);
      });

      if (this.companionOptions) this.companionOptions.hidden = !['pair', 'complete'].includes(mode);
      if (this.balloonOptions) this.balloonOptions.hidden = !['balloons', 'complete'].includes(mode);
      if (this.note) this.note.hidden = ['single', 'balloons'].includes(mode);
      if (this.dynamicCheckout) this.dynamicCheckout.hidden = mode !== 'single';

      if (['balloons', 'complete'].includes(mode) && this.balloonColour && this.balloonNumber) {
        await this.onBalloonChange(false);
      }

      this.clearBundleOrderProperties();
      this.render();

      if (revealDetails) this.scrollExpandedDetailsIntoView(mode);
    }

    scrollExpandedDetailsIntoView(mode) {
      if (mode === 'single') return;

      let target = this.balloonOptions;

      if (['pair', 'complete'].includes(mode)) {
        target = this.note && !this.note.hidden ? this.note : this.companionOptions;
      }

      if (!target || target.hidden) return;

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          const rect = target.getBoundingClientRect();
          const bottomGap = window.innerWidth < 750 ? 24 : 32;
          const scrollDistance = rect.bottom - (window.innerHeight - bottomGap);

          if (scrollDistance <= 0) return;

          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          window.scrollBy({
            top: Math.ceil(scrollDistance),
            behavior: reducedMotion ? 'auto' : 'smooth',
          });
        });
      });
    }

    clearBundleOrderProperties() {
      if (!this.form) return;
      // Bundle UI state stays in this component, not in Shopify order properties.
      Array.from(this.form.elements).forEach((input) => {
        const match = input.name?.match(/^properties\[(.+)\]$/);
        if (match && /^_Dyefor\b|^_Matches design$/i.test(match[1])) input.remove();
      });
    }

    async onBalloonChange(forceReload) {
      if (!this.balloonColour || !this.balloonNumber) {
        this.selectInitialBalloonVariant();
        this.clearBundleOrderProperties();
        this.render();
        return;
      }

      this.setAttribute('aria-busy', 'true');
      try {
        const product = await this.loadBalloonProduct(this.balloonColour.value, forceReload);
        const selectedNumber = String(this.balloonNumber.value);
        this.balloonProduct = product;
        this.balloonVariant = product.variants.find((variant) => {
          const option = variant.option1 ?? variant.options?.[0] ?? variant.title;
          return String(option) === selectedNumber && variant.available !== false;
        });

        if (!this.balloonVariant) throw new Error('That balloon number is not available.');
        this.balloonPrice = Number(this.balloonVariant.price || 0);
        this.updateBalloonPreview(this.balloonVariant, product);
        this.clearBundleOrderProperties();
        this.clearError();
      } catch (error) {
        this.balloonVariant = null;
        this.showError(error.message || 'The balloon options could not be loaded.');
      } finally {
        this.removeAttribute('aria-busy');
        this.render();
      }
    }

    async loadBalloonProduct(handle, forceReload = false) {
      if (!forceReload && this.balloonProducts.has(handle)) return this.balloonProducts.get(handle);

      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}products/${handle}.js`, {
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) throw new Error('The selected balloon is currently unavailable.');
      const product = await response.json();
      this.balloonProducts.set(handle, product);
      return product;
    }

    imageSource(variant, product) {
      return variant?.featured_image?.src
        || variant?.featured_image
        || product?.featured_image?.src
        || product?.featured_image
        || product?.images?.[0]?.src
        || product?.images?.[0]
        || '';
    }

    updateBalloonPreview(variant, product) {
      const source = this.imageSource(variant, product);
      if (!source) return;

      this.querySelectorAll('[data-balloon-preview] img').forEach((image) => {
        image.src = source;
        image.removeAttribute('srcset');
      });
    }

    updateCompanionPreview(variant) {
      const source = this.imageSource(variant, this.companionProduct);
      if (!source) return;

      this.querySelectorAll('[data-companion-preview] img').forEach((image) => {
        image.src = source;
        image.removeAttribute('srcset');
      });
    }

    captureGalleryRestoreMedia(force = false) {
      if (!this.gallery) return;

      const activeMedia = typeof this.gallery.getActiveMedia === 'function'
        ? this.gallery.getActiveMedia()
        : this.gallery.querySelector('[data-media-id].is-active');

      if (!activeMedia || activeMedia.hasAttribute('data-party-bundle-gallery-media')) return;
      if (force || !this.restoreMediaId) this.restoreMediaId = activeMedia.dataset.mediaId || null;
    }

    galleryImageDetails(variant, product) {
      const variantImage = variant?.featured_image && typeof variant.featured_image === 'object'
        ? variant.featured_image
        : null;
      const productImage = product?.featured_image && typeof product.featured_image === 'object'
        ? product.featured_image
        : null;
      const featuredImage = variantImage || productImage;
      const source = this.imageSource(variant, product);
      const width = Number(featuredImage?.width || 1200);
      const height = Number(featuredImage?.height || width);
      const aspectRatio = Number(featuredImage?.aspect_ratio || (width / height) || 1);

      return {
        source,
        width,
        height,
        aspectRatio: Number.isFinite(aspectRatio) && aspectRatio > 0 ? aspectRatio : 1,
        alt: product?.title || variant?.title || 'Selected bundle item'
      };
    }

    createGalleryPreviewMedia() {
      const viewer = this.gallery?.elements?.viewer;
      const list = viewer?.querySelector('[id^="Slider-Gallery-"]');
      if (!list) return null;

      const item = document.createElement('li');
      item.id = `Slide-${this.dataset.sectionId || 'product'}-PartyBundlePreview`;
      item.className = 'product__media-item grid__item slider__slide dyefor-party-bundle__gallery-media';
      item.dataset.mediaId = this.galleryPreviewId;
      item.setAttribute('data-party-bundle-gallery-media', '');

      const opener = document.createElement('div');
      opener.className = 'product__modal-opener product__modal-opener--image no-js-hidden';

      const frame = document.createElement('div');
      frame.className = 'product__media media media--transparent gradient global-media-settings';
      frame.setAttribute('data-party-bundle-gallery-frame', '');

      const image = document.createElement('img');
      image.setAttribute('data-party-bundle-gallery-image', '');
      image.loading = 'eager';
      image.fetchPriority = 'high';
      image.decoding = 'async';

      frame.appendChild(image);
      opener.appendChild(frame);
      item.appendChild(opener);
      list.appendChild(item);
      viewer.resetPages?.();
      return item;
    }

    createGalleryPreviewThumbnail(details) {
      const thumbnails = this.gallery?.elements?.thumbnails;
      const list = thumbnails?.querySelector('[id^="Slider-Thumbnails-"]');
      if (!list) return null;

      const item = document.createElement('li');
      item.id = `Slide-Thumbnails-${this.dataset.sectionId || 'product'}-PartyBundlePreview`;
      item.className = 'thumbnail-list__item slider__slide dyefor-party-bundle__gallery-thumbnail';
      item.dataset.target = this.galleryPreviewId;
      item.dataset.mediaPosition = String(
        this.gallery.elements.viewer.querySelectorAll('[data-media-id]').length
      );

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'thumbnail global-media-settings global-media-settings--no-shadow thumbnail--narrow';
      button.setAttribute('aria-controls', `GalleryViewer-${this.dataset.sectionId}`);
      button.setAttribute('aria-label', `Show ${details.alt}`);
      button.addEventListener('click', () => {
        this.activateGalleryMedia(this.galleryPreviewId, false);
      });

      const image = document.createElement('img');
      image.setAttribute('data-party-bundle-gallery-thumbnail-image', '');
      image.loading = 'lazy';
      image.width = 200;
      image.height = 200;
      image.src = details.source;
      image.alt = details.alt;

      button.appendChild(image);
      item.appendChild(button);
      list.appendChild(item);
      thumbnails.resetPages?.();
      return item;
    }

    activateGalleryMedia(mediaId, prepend = true) {
      const viewer = this.gallery?.elements?.viewer;
      if (!viewer) return false;

      const mediaItems = Array.from(viewer.querySelectorAll('[data-media-id]'));
      const activeMedia = mediaItems.find((item) => item.dataset.mediaId === mediaId);
      if (!activeMedia) return false;

      mediaItems.forEach((item) => item.classList.remove('is-active'));
      activeMedia.classList.add('is-active');
      activeMedia.style.removeProperty('display');

      if (prepend) activeMedia.parentElement?.prepend(activeMedia);
      viewer.resetPages?.();

      const thumbnails = this.gallery.elements?.thumbnails;
      const thumbnailItems = thumbnails
        ? Array.from(thumbnails.querySelectorAll('[data-target]'))
        : [];
      const activeThumbnail = thumbnailItems.find((item) => item.dataset.target === mediaId);
      if (prepend && activeThumbnail) activeThumbnail.parentElement?.prepend(activeThumbnail);
      if (activeThumbnail) this.gallery.setActiveThumbnail?.(activeThumbnail);
      thumbnails?.resetPages?.();
      this.gallery.playActiveMedia?.(activeMedia);
      return true;
    }

    showGalleryPreview(variant, product, suppliedDetails = null) {
      if (!this.gallery) return;

      const details = suppliedDetails || this.galleryImageDetails(variant, product);
      if (!details.source) {
        this.restoreGalleryMedia();
        return;
      }

      this.captureGalleryRestoreMedia();

      const viewer = this.gallery.elements?.viewer;
      let media = viewer?.querySelector('[data-party-bundle-gallery-media]');
      if (!media) media = this.createGalleryPreviewMedia();
      if (!media) return;

      media.style.removeProperty('display');
      media.toggleAttribute(
        'data-party-bundle-balloon-preview',
        this.selectedMode === 'balloons' || (this.selectedMode === 'complete' && !suppliedDetails)
      );
      const frame = media.querySelector('[data-party-bundle-gallery-frame]');
      const image = media.querySelector('[data-party-bundle-gallery-image]');
      if (frame) frame.style.paddingTop = `${100 / details.aspectRatio}%`;
      if (image) {
        image.src = details.source;
        image.alt = details.alt;
        image.width = details.width;
        image.height = details.height;
        image.removeAttribute('srcset');
      }

      let thumbnail = this.gallery.elements?.thumbnails?.querySelector(
        `[data-target="${this.galleryPreviewId}"]`
      );
      if (!thumbnail) thumbnail = this.createGalleryPreviewThumbnail(details);
      const thumbnailImage = thumbnail?.querySelector('[data-party-bundle-gallery-thumbnail-image]');
      if (thumbnailImage) {
        thumbnailImage.src = details.source;
        thumbnailImage.alt = details.alt;
        thumbnailImage.removeAttribute('srcset');
      }

      this.activateGalleryMedia(this.galleryPreviewId, true);
    }

    removeGalleryPreview(resetPages = true) {
      if (!this.gallery) return;

      this.gallery.elements?.viewer
        ?.querySelector('[data-party-bundle-gallery-media]')
        ?.remove();
      this.gallery.elements?.thumbnails
        ?.querySelector(`[data-target="${this.galleryPreviewId}"]`)
        ?.remove();

      if (resetPages) {
        this.gallery.elements?.viewer?.resetPages?.();
        this.gallery.elements?.thumbnails?.resetPages?.();
      }
    }

    restoreGalleryMedia() {
      if (!this.gallery) return;

      const restoreMediaId = this.restoreMediaId;
      const previewMedia = this.gallery.elements?.viewer
        ?.querySelector('[data-party-bundle-gallery-media]');
      if (!previewMedia && !restoreMediaId) return;

      this.removeGalleryPreview(false);

      const viewer = this.gallery.elements?.viewer;
      const restoreMedia = restoreMediaId
        ? viewer?.querySelector(`[data-media-id="${restoreMediaId}"]`)
        : null;
      const fallbackMedia = restoreMedia || viewer?.querySelector('[data-media-id]');

      if (fallbackMedia) {
        this.activateGalleryMedia(fallbackMedia.dataset.mediaId, true);
      } else {
        viewer?.resetPages?.();
        this.gallery.elements?.thumbnails?.resetPages?.();
      }

      this.restoreMediaId = null;
    }

    syncGalleryMedia(companionVariant) {
      // Companion mockups are not generated yet. Keep the real AI media (and its zoom) in every mode.
      if (this.isAIDesignProduct) {
        this.restoreGalleryMedia();
        const media = this.getAIGalleryPreview()?.closest('[data-media-id]');
        if (media && !media.classList.contains('is-active')) {
          this.activateGalleryMedia(media.dataset.mediaId, true);
        }
        return;
      }

      const mode = this.selectedMode;
      if (mode === 'single') {
        this.restoreGalleryMedia();
        return;
      }

      if (mode === 'pair') {
        this.showGalleryPreview(
          companionVariant,
          this.companionProduct,
          this.bundleGalleryImages.pair || null
        );
        return;
      }

      if (mode === 'complete') {
        this.showGalleryPreview(
          this.balloonVariant,
          this.balloonProduct,
          this.bundleGalleryImages.complete || null
        );
        return;
      }

      if (mode === 'balloons') {
        this.showGalleryPreview(
          this.balloonVariant,
          this.balloonProduct,
          this.bundleGalleryImages.balloons || null
        );
      }
    }

    render() {
      this.refreshMainVariant();
      this.syncSizeState();
      const companionVariant = this.getCompanionVariant();
      const mainPrice = Number(this.mainVariant?.price || 0);
      const companionPrice = Number(companionVariant?.price || 0);
      const totals = {
        single: mainPrice,
        pair: mainPrice + companionPrice,
        balloons: mainPrice + Number(this.balloonPrice || 0),
        complete: mainPrice + companionPrice + Number(this.balloonPrice || 0)
      };

      this.updateCompanionPreview(companionVariant);
      this.syncGalleryMedia(companionVariant);

      Object.entries(totals).forEach(([mode, total]) => {
        const price = this.querySelector(`[data-bundle-total="${mode}"]`);
        const shipping = this.querySelector(`[data-shipping-badge="${mode}"]`);
        if (price) price.textContent = this.formatMoney(total);
        if (shipping) shipping.hidden = total < this.freeShippingThreshold;
      });
    }

    formatMoney(cents) {
      const currency = window.Shopify?.currency?.active || 'GBP';
      const locale = document.documentElement.lang || 'en-GB';

      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: 2
        }).format(cents / 100);
      } catch (error) {
        return `GBP ${(cents / 100).toFixed(2)}`;
      }
    }

    collectPersonalisationProperties(form) {
      const properties = {};
      form.querySelectorAll('[name^="properties["]').forEach((input) => {
        if (input.disabled) return;
        if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return;
        if (input.type === 'file') return;

        const match = input.name.match(/^properties\[(.+)\]$/);
        const value = input.value?.trim();
        if (match && value && !/^_Dyefor\b|^_Matches design$/i.test(match[1])) properties[match[1]] = value;
      });
      return properties;
    }

    async addSelectedItems(form, { sections = [], sectionsUrl = window.location.pathname } = {}) {
      const mode = this.selectedMode;
      if (mode === 'single') return null;

      this.clearError();
      const mainVariantId = Number(form?.querySelector('[name="id"]')?.value);
      const mainVariant = this.mainVariants.find((variant) => Number(variant.id) === mainVariantId);
      if (!mainVariant?.id || mainVariant.available === false) {
        throw new Error('The selected item is currently unavailable. Please choose another option.');
      }

      const needsCompanion = ['pair', 'complete'].includes(mode);
      const needsBalloon = ['balloons', 'complete'].includes(mode);
      const companionVariant = this.getCompanionVariant();
      if (needsCompanion && (!companionVariant?.id || companionVariant.available === false)) {
        throw new Error('The matching item is currently unavailable. Please choose another bundle.');
      }

      if (needsBalloon) {
        if (!this.balloonVariant && this.balloonColour) await this.onBalloonChange(false);
        if (!this.balloonVariant?.id || this.balloonVariant.available === false) {
          throw new Error('The selected balloon is currently unavailable. Please choose another option.');
        }

      }

      this.mainVariant = mainVariant;
      this.clearBundleOrderProperties();
      const personalisation = this.collectPersonalisationProperties(form);
      const requestedQuantity = Number.parseInt(form?.querySelector('[name="quantity"]')?.value, 10);
      const mainQuantity = Number.isFinite(requestedQuantity) && requestedQuantity > 0 ? requestedQuantity : 1;
      const items = [
        {
          id: mainVariantId,
          quantity: mainQuantity,
          properties: personalisation
        }
      ];

      if (needsCompanion) {
        items.push({
          id: Number(companionVariant.id),
          quantity: 1,
          properties: { ...personalisation }
        });
      }

      if (needsBalloon) {
        items.push({
          id: Number(this.balloonVariant.id),
          quantity: 1,
          properties: { ...personalisation }
        });
      }

      const requestBody = { items };
      const requestedSections = Array.isArray(sections) ? sections.filter(Boolean) : [];
      if (requestedSections.length) {
        requestBody.sections = requestedSections;
        requestBody.sections_url = sectionsUrl;
      }

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(requestBody)
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        const message = result.description || result.message || 'The matching items could not be added.';
        this.showError(message);
        throw new Error(message);
      }

      const mainItem = result.items?.find((item) => Number(item.variant_id || item.id) === mainVariantId)
        || result.items?.[0];
      if (mainItem) {
        result.id = mainItem.id;
        result.key = mainItem.key;
      }

      return result;
    }

    showError(message) {
      if (!this.errorElement) return;
      this.errorElement.textContent = message;
      this.errorElement.hidden = false;
    }

    clearError() {
      if (!this.errorElement) return;
      this.errorElement.textContent = '';
      this.errorElement.hidden = true;

      if (this.productForm && !this.productForm.submitButton?.disabled) {
        this.productForm.error = false;
        this.productForm.submitButton?.removeAttribute('aria-disabled');
      }
    }
  });
}
